import { useState } from "react";
import { Link } from "react-router-dom";
import { i18n } from "./i18n";
import { legalConfigReady } from "./lib/legal";

export type SchoolPrivacyProfile = {
  organizationName: string;
  contactNameOrTitle: string;
  contactEmail: string;
  contactAddress: string;
};

export default function LegalAcceptanceGate({
  busy,
  onAccept,
}: {
  busy: boolean;
  onAccept: (profile: SchoolPrivacyProfile) => Promise<void>;
}) {
  const [read, setRead] = useState(false);
  const [authority, setAuthority] = useState(false);
  const [profile, setProfile] = useState<SchoolPrivacyProfile>({
    organizationName: "",
    contactNameOrTitle: "",
    contactEmail: "",
    contactAddress: "",
  });
  const [error, setError] = useState("");
  async function accept() {
    try {
      setError("");
      await onAccept(profile);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : i18n.t("screenCopy:legalAcceptanceGate.couldNotRecordAcceptanceTryAgain"),
      );
    }
  }
  const complete = Object.values(profile).every((item) => item.trim());
  const update = (key: keyof SchoolPrivacyProfile, value: string) =>
    setProfile((current) => ({ ...current, [key]: value }));
  return (
    <main className="legal-gate" id="main-content">
      <section className="paper-card">
        <div className="register-mark">私</div>
        <h1>{i18n.t("screenCopy:legalAcceptanceGate.setUpTheCollectionNoticeBeforeUsing")}</h1>
        <p>{i18n.t("screenCopy:legalAcceptanceGate.theseDetailsAppearBeforeStudentsJoinQuizzes")}</p>
        {!legalConfigReady && (
          <p className="form-error" role="alert">
            {i18n.t("screenCopy:legalAcceptanceGate.theOperatorSLegalContactDetailsAre")}
          </p>
        )}
        <div className="privacy-profile-fields">
          <label>
            {i18n.t("screenCopy:legalAcceptanceGate.schoolDataUserName")}
            <input
              required
              value={profile.organizationName}
              onChange={(event) => update("organizationName", event.target.value)}
            />
          </label>
          <label>
            {i18n.t("screenCopy:legalAcceptanceGate.personOrTitleHandlingAccessAndCorrection")}
            <input
              required
              value={profile.contactNameOrTitle}
              onChange={(event) => update("contactNameOrTitle", event.target.value)}
              placeholder={i18n.t("screenCopy:legalAcceptanceGate.eGDataProtectionOfficer")}
            />
          </label>
          <label>
            {i18n.t("screenCopy:legalAcceptanceGate.contactEmail")}
            <input
              type="email"
              required
              value={profile.contactEmail}
              onChange={(event) => update("contactEmail", event.target.value)}
            />
          </label>
          <label>
            {i18n.t("screenCopy:legalAcceptanceGate.contactAddress")}
            <textarea
              required
              rows={2}
              value={profile.contactAddress}
              onChange={(event) => update("contactAddress", event.target.value)}
            />
          </label>
        </div>
        <label className="legal-check">
          <input type="checkbox" checked={read} onChange={(event) => setRead(event.target.checked)} />
          <span>
            {i18n.t("screenCopy:legalAcceptanceGate.iHaveReadAndAccept")}
            <Link to="/terms" target="_blank">
              {i18n.t("landing:termsLink")}
            </Link>
            、
            <Link to="/privacy" target="_blank">
              {i18n.t("legalPolicy:privacyLink")}
            </Link>
            {i18n.t("screenCopy:legalAcceptanceGate.and")}
            <Link to="/data-processing" target="_blank">
              {i18n.t("legalPolicy:dataProcessingLink")}
            </Link>
            。
          </span>
        </label>
        <label className="legal-check">
          <input type="checkbox" checked={authority} onChange={(event) => setAuthority(event.target.checked)} />
          <span>{i18n.t("screenCopy:legalAcceptanceGate.iConfirmSchoolAuthorisationToProcessAnd")}</span>
        </label>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button
          className="primary-action large"
          disabled={!legalConfigReady || !complete || !read || !authority || busy}
          onClick={() => void accept()}
        >
          {busy
            ? i18n.t("screenCopy:legalAcceptanceGate.recording")
            : i18n.t("screenCopy:legalAcceptanceGate.saveNoticeAndConfirm")}
        </button>
        <small>{i18n.t("screenCopy:legalAcceptanceGate.schoolContactsAcceptanceDateAndTermsVersions")}</small>
      </section>
    </main>
  );
}
